import { VISCOSITY_TEMPERATURE_TYPE } from "@/libs/calculations/constants";
import type { OilViscosityParameters, ViscosityTest } from "../../d";
import { andradeOilViscosity } from "./andradeOilViscosity";
import type { LagrangeInterpolationDataWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";
import { evaluateLagrangePolynomialWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";

export function deadOrLiveOilViscositiesCalculator(
  params: OilViscosityParameters,
  oilType: string
): {
  oilViscosity: number;
  a1: number;
  b1: number;
  a2: number;
  b2: number;
} {
  const { customViscosityProperties, inSituPressurePSIA, temperatureF } =
    params;
  const oilTypeKey = oilType as keyof typeof customViscosityProperties;
  if (
    customViscosityProperties &&
    customViscosityProperties[oilTypeKey] !== null &&
    customViscosityProperties[oilTypeKey] !== undefined
  ) {
    const oilViscosityData: LagrangeInterpolationDataWithGroupings =
      customViscosityProperties[oilTypeKey];
    // If there is only one grouping, there have to be 1, 2, or 3 points
    if (oilViscosityData.dataGroupings.length === 1) {
      // Double check points again, keep else in here just in case
      if (oilViscosityData?.dataGroupings[0].points.length < 4) {
        const pseudoViscosityTests: ViscosityTest[] = [];
        const useTemperature =
          oilViscosityData?.type === VISCOSITY_TEMPERATURE_TYPE;
        // Make set of viscosity tests depending on if temperature or pressure
        for (const point of oilViscosityData?.dataGroupings[0].points) {
          if (useTemperature) {
            pseudoViscosityTests.push({
              oilCp: point.y,
              testTemperatureF: point.x
            });
          } else {
            pseudoViscosityTests.push({
              oilCp: point.y,
              testPressurePSIA: point.x
            });
          }
        }
        // Override the viscosity tests in params
        params = { ...params, viscosityTests: pseudoViscosityTests };
        return andradeOilViscosity(params, useTemperature);
      } else {
        // I don't think we should ever get here?
        const oilViscosity = evaluateLagrangePolynomialWithGroupings(
          oilViscosityData,
          inSituPressurePSIA,
          temperatureF
        );
        return { oilViscosity, a1: 0, b1: 0, a2: 0, b2: 0 };
      }
    } else if (oilViscosityData.dataGroupings.length > 1) {
      // If there's more than one grouping we have more than 3 data points
      // and need to use lagrange
      const oilViscosity = evaluateLagrangePolynomialWithGroupings(
        oilViscosityData,
        inSituPressurePSIA,
        temperatureF
      );
      return { oilViscosity, a1: 0, b1: 0, a2: 0, b2: 0 };
    } else {
      // If we reach this point, it means we have insufficient data
      return { oilViscosity: 0, a1: 0, b1: 0, a2: 0, b2: 0 };
    }
  }
  return { oilViscosity: 0, a1: 0, b1: 0, a2: 0, b2: 0 };
}
